import SimplePeer from 'simple-peer';
import { User, X5Lesson } from '../../../domain';

export const ClassroomRoles = ['Teacher', 'Student'] as const;
export type ClassroomRole = typeof ClassroomRoles[number];

export const ClassroomStatuses = [
    'Scheduled',
    'InProgress',
    'Cancelled',
    'Absent',
    'Indeterminate',
    'ServerIssues',
    'ClientIssues',
    'Completed'
] as const;

export type ClassroomStatus = typeof ClassroomStatuses[number];

export const ClassroomStatusLabels: { readonly [K in ClassroomStatus]: string } = {
    Scheduled: 'Scheduled',
    InProgress: 'In progress',
    Cancelled: 'Cancelled',
    Absent: 'Absent',
    Indeterminate: 'Indeterminate',
    ServerIssues: 'Technical issues on server',
    ClientIssues: 'Technical issues on client',
    Completed: 'Completed'
};

export interface Classroom {
    readonly Id: number;

    readonly TeacherId: number;
    readonly Teacher: User | null;

    readonly StudentId: number;
    readonly Student: User | null;

    readonly ScheduledStartTime: number;

    readonly LessonId: number;
    readonly Lesson: X5Lesson | null;

    readonly ActualStartTime: number | null;
    readonly ActualEndTime: number | null;

    readonly Status: ClassroomStatus;

    readonly Events: readonly ClassroomEvent[] | null;
    readonly Interruptions: readonly ClassroomInterruption[] | null;

    readonly SuggestionStartTime: number | null;
    readonly SuggestionUserId: number | null;
}

export interface ClassroomEvent {
    readonly Id: number;
    readonly Timestamp: number;
    readonly Message: string;
}

export interface ClassroomInterruption {
    readonly UserId: number;
    readonly UnavailableStart: number;
    readonly UnavailableEnd: number;
}

export interface ClassroomState {
    readonly ClassroomId: number;

    readonly Version: number;

    readonly ClassroomStatus: ClassroomStatus;

    readonly TeacherFirstSeen: number | null;
    readonly TeacherLastSeen: number | null;
    readonly TeacherConnectionId: string | null;
    readonly TeacherMediaState: string | null;

    readonly StudentFirstSeen: number | null;
    readonly StudentLastSeen: number | null;
    readonly StudentConnectionId: string | null;
    readonly StudentMediaState: string | null;

    readonly LessonSnapshotId: number;
    readonly CurrentPageId: number | null;
    readonly CurrentPushableBlockId: number | null;
}

export interface ClassroomKvEntry {
    readonly Author: number;
    readonly ChangeStamp: number;
    readonly Key: string;
    readonly Value: string;
}

export interface ClassroomKvEntryUpdateModel {
    readonly Author: number;
    readonly Key: string;
    readonly Value: string;
}

/******************************************************************************/

export interface ClassroomBadRequestMessage {
    readonly MessageType: 'bad-request';
    readonly Error: string;
}

export interface ClassroomDisconnectMessage {
    readonly MessageType: 'disconnect';
    readonly Error: string;
}

export interface ClassroomReconnectMessage {
    readonly MessageType: 'reset-connection';
    readonly Error: string;
}

export interface ClassroomStateMessage {
    readonly MessageType: 'classroom-state';
    readonly ClassroomState: ClassroomState;
}

export interface ClassroomKeyValuesUpdatedMessage {
    readonly MessageType: 'classroom-key-values-updated';
    readonly ClassroomState: ClassroomState;
    readonly ChangeStamps: readonly number[];
    readonly Changes: readonly ClassroomKvEntry[];
}

export interface ClassroomKeyValuesPartMessage {
    readonly MessageType: 'classroom-key-values-part';
    readonly ChangeStamps: readonly number[];
    readonly KeyValues: readonly ClassroomKvEntry[];
}

export interface TurnServerCredential {
    readonly Username: string;
    readonly Password: string;
}

export interface ClassroomJoinedMessage {
    readonly MessageType: 'classroom-joined';
    readonly JoinedAs: ClassroomRole;
    readonly ClassroomState: ClassroomState;
    readonly KeyValues: readonly ClassroomKvEntry[];
    readonly Credential: TurnServerCredential;
}

export interface ClassroomDebugMessage {
    readonly MessageType: 'debug';
    readonly Payload: any;
}

export interface SimplePeerSignalMessage {
    readonly MessageType: 'simple-peer-signal';
    readonly Signal: SimplePeerSignal;
}

export interface ClassroomContentUpdatedMessage {
    readonly MessageType: 'classroom-content-updated';
}

export type ClassroomHubMessage = ClassroomBadRequestMessage
    | ClassroomReconnectMessage
    | ClassroomDisconnectMessage
    | ClassroomJoinedMessage
    | ClassroomStateMessage
    | ClassroomKeyValuesUpdatedMessage
    | ClassroomKeyValuesPartMessage
    | ClassroomDebugMessage
    | SimplePeerSignalMessage
    | ClassroomContentUpdatedMessage;
/* OR AnyOtherMessage with MessageType discriminating property */

/******************************************************************************/

export interface ClassroomUserFilter {
    Filter?: string;
    UserId?: number;
}

export interface ClassroomSearchDto {
    Page: number;
    Teacher: ClassroomUserFilter;
    Student: ClassroomUserFilter;
    StartDate: string;
    Status: ClassroomStatus | null;
    EndDate: string;
}

export interface ClassroomUserSearchDto {
    Filter: string;
    Page?: number;
    // Unix Epoch Seconds
    AvailableOn?: number;
}

export interface ClassroomUpdateModel {
    TeacherId: number,
    StudentId: number,
    StartTime: number;
    LessonId: number;
    PendingTime: number;
    Status: ClassroomStatus;
}

export interface ClassroomEditLessonModel {
    LessonId: number;
}

/******************************************************************************/

export type SimplePeerInstance = SimplePeer.Instance & {
    readonly connected: boolean;
};

export interface SimplePeerSignal {
    readonly From: string;
    readonly To: string;
    readonly Data: string;
}

/******************************************************************************/
// Teacher Student Models

export interface StudentDetailsResponse {
    StudentId: number;
    FirstName: string;
    LastName: string;
    Email: string;
    TimeZoneName: string | null;
    LevelName: string | null;
    AgeCategoryName: string | null;
    LanguageName: string | null;
    Balance: number;
    CompletedLessons: number;
    Exams: StudentDetailExamItem[];
    Essays: StudentDetailEssayItem[];
}

export interface StudentDetailExamItem {
    ExamId: number;
    LessonName: string;
    UnitName: string;
    Status: string;
    Bal: number | null;
    CreatedAt: string;
    FinishedAt: string | null;
}

export interface StudentDetailEssayItem {
    EssayId: number;
    Title: string;
    Status: string;
    Rating: number | null;
    CreatedAt: string;
    FinishedAt: string | null;
}

export interface ExaminationLessonItem {
    LessonId: number;
    LessonName: string;
    UnitId: number;
    UnitName: string;
}

export interface CreateExamRequest {
    StudentId: number;
    LessonId: number;
    TimeLimitMinutes?: number | null;
}

export interface StudentPerformanceItem {
    StudentId: number;
    FirstName: string;
    LastName: string;
    LevelName: string | null;
    AgeCategoryName: string | null;
    LanguageName: string | null;
    TimeZoneName: string | null;
    ExamCount: number;
    TotalBal: number;
    AverageBal: number;
}